/**
 * SOLID Review
 * S — Strong. One job: hold and broadcast a single boolean (advertising consent granted/denied). The
 *     file-level comment explains exactly why it exists as a standalone module — to avoid a circular
 *     dependency between ad clients and ConsentGateway.
 * O — Strong. A get/set/subscribe signal has no real extension axis; nothing here would need to change to
 *     accommodate new behavior. N/A /
 * L — N/A — no inheritance hierarchy, not even a class (plain module-level state + functions).
 * I — Strong. Four tiny functions (set/get/subscribe/reset), each independently usable — no caller is
 *     forced to depend on a method it doesn't need.
 * D — Acceptable. Zero imports at all — pure module-scope state. Nothing here to violate D; it isn't
 *     defined behind an application-layer port, but it's simple enough that no consumer needs one — noted
 *     plainly, not a defect.
 */
/**
 * Process-wide advertising-consent signal.
 *
 * Decoupled from {@link ConsentGateway} so the low-level ad clients
 * (AdSenseClient / PropellerAdsClient) can gate script injection on it without a
 * circular dependency on the gateway or React state.  Defaults to DENIED — no
 * advertising script may load until the CMP explicitly grants consent.
 */
let advertisingConsentGranted = false;
const listeners = new Set<(granted: boolean) => void>();

export function setAdvertisingConsentGranted(granted: boolean): void {
  if (granted === advertisingConsentGranted) return;
  advertisingConsentGranted = granted;
  for (const listener of listeners) {
    listener(granted);
  }
}

export function isAdvertisingConsentGranted(): boolean {
  return advertisingConsentGranted;
}

export function subscribeAdvertisingConsent(listener: (granted: boolean) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test-only reset to restore the default-denied state between cases. */
export function __resetAdConsentSignal(): void {
  advertisingConsentGranted = false;
  listeners.clear();
}
